/* ============================================================
   ps-chapters.js — P.S. beauty by peakspan
   The interaction motto for the full-screen chapter homepage.
   Loaded deferred, AFTER main.js / wow.js / ps-motion.js.

   1. Count-up for every big numeral (decimals, suffixes, grouping).
      wow.js only drives the counters inside a .wsb band — this takes
      over every other .wow-count / .psc-count on the page.
   2. Hero gallery thumbs auto-activate on hover intent (120 ms).
   3. The wavelength roster cycles: one card leads at a time.
   4. Extra alternating left/right scroll reveals for the elements
      ps-motion.js does not already handle.

   Additive only. Reduced motion: final values, no movement.
   No-JS: everything is already visible with its final numbers.
   ============================================================ */
(function () {
  'use strict';

  var reduced = false;
  try {
    reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch (e) { reduced = false; }

  var hasIO = 'IntersectionObserver' in window;

  function each(list, fn) {
    Array.prototype.forEach.call(list, fn);
  }

  /* ----------------------------------------------------------
     1 · COUNT-UP
     <span class="wow-count" data-target="36">36</span>%
     Optional: data-decimals="1"  data-group="1"
               data-prefix="~"    data-suffix=" nm"
     ---------------------------------------------------------- */
  function group(intPart) {
    return intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function format(el, value) {
    var dec = parseInt(el.getAttribute('data-decimals') || '0', 10);
    if (isNaN(dec) || dec < 0) { dec = 0; }
    var text = dec > 0 ? value.toFixed(dec) : String(Math.round(value));
    if (el.getAttribute('data-group') === '1') {
      var parts = text.split('.');
      parts[0] = group(parts[0]);
      text = parts.join('.');
    }
    return (el.getAttribute('data-prefix') || '') + text + (el.getAttribute('data-suffix') || '');
  }

  function setFinal(el) {
    var target = parseFloat(el.getAttribute('data-target'));
    if (isNaN(target)) { return; }
    el.textContent = format(el, target);
  }

  function countUp(el) {
    var target = parseFloat(el.getAttribute('data-target'));
    if (isNaN(target)) { return; }
    var duration = 1400;
    var start = null;
    el.textContent = format(el, 0);
    function tick(now) {
      if (start === null) { start = now; }
      var t = Math.min((now - start) / duration, 1);
      var eased = 1 - Math.pow(1 - t, 3); /* easeOutCubic */
      if (t < 1) {
        el.textContent = format(el, target * eased);
        window.requestAnimationFrame(tick);
      } else {
        el.textContent = format(el, target);
      }
    }
    window.requestAnimationFrame(tick);
  }

  var counters = [];
  each(document.querySelectorAll('.wow-count, .psc-count'), function (el) {
    /* wow.js owns the counters inside its dark stat band */
    if (el.closest && el.closest('.wsb')) { return; }
    counters.push(el);
  });

  if (counters.length) {
    if (reduced || !hasIO) {
      counters.forEach(setFinal);
    } else {
      var countIO = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          if (!en.isIntersecting) { return; }
          countIO.unobserve(en.target);
          countUp(en.target);
        });
      }, { threshold: 0.4, rootMargin: '0px 0px -6% 0px' });
      counters.forEach(function (el) { countIO.observe(el); });
    }
  }

  /* ----------------------------------------------------------
     2 · HERO THUMBS — hover intent activates the slide
     main.js already owns the click handler; we just fire it.
     Pointer must be a real mouse, and only after 120 ms of intent.
     ---------------------------------------------------------- */
  var finePointer = false;
  try {
    finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  } catch (e2) { finePointer = false; }

  if (finePointer) {
    each(document.querySelectorAll('.hero-thumbs button'), function (btn) {
      var timer = null;
      btn.addEventListener('mouseenter', function () {
        window.clearTimeout(timer);
        timer = window.setTimeout(function () {
          if (!btn.classList.contains('active')) { btn.click(); }
        }, 120);
      });
      btn.addEventListener('mouseleave', function () {
        window.clearTimeout(timer);
      });
    });
  }

  /* ----------------------------------------------------------
     3 · WAVELENGTH ROSTER — one card leads at a time
     The gentle left/right sway is CSS (@keyframes ps-sway);
     this only moves the "lead" highlight, pauses on hover/focus,
     and stops while the roster is off-screen.
     ---------------------------------------------------------- */
  each(document.querySelectorAll('.psc-roster'), function (roster) {
    var cards = roster.querySelectorAll('.wave');
    if (cards.length < 2) { return; }

    var index = 0;
    var paused = false;
    var visible = !hasIO;

    function lead() {
      each(cards, function (card, i) {
        card.classList.toggle('is-lead', i === index);
      });
    }
    lead();

    if (reduced) { return; }

    each(cards, function (card, i) {
      card.addEventListener('mouseenter', function () { index = i; lead(); });
      card.addEventListener('focusin', function () { index = i; lead(); });
    });
    roster.addEventListener('mouseenter', function () { paused = true; });
    roster.addEventListener('mouseleave', function () { paused = false; });
    roster.addEventListener('focusin', function () { paused = true; });
    roster.addEventListener('focusout', function () { paused = false; });

    if (hasIO) {
      new IntersectionObserver(function (entries) {
        visible = entries[0].isIntersecting;
      }, { threshold: 0.2 }).observe(roster);
    }

    window.setInterval(function () {
      if (paused || !visible) { return; }
      index = (index + 1) % cards.length;
      lead();
    }, 3200);
  });

  /* ----------------------------------------------------------
     4 · EXTRA SCROLL REVEALS — alternating left / right
     ps-motion.js already handles the original card rosters; this
     picks up the ones introduced by the chapter layout. Elements
     it already tagged are skipped.
     ---------------------------------------------------------- */
  if (reduced || !hasIO) { return; }

  var revealSel = [
    '.psc-roster .wave',
    '#results .stat',
    '#results .psc-pillrow a',
    '#science .sci-card',
    '#rituals .tl-item',
    '#certifications .wcert-chip',
    '.psc-spectables .psc-tablewrap',
    '.psc-how-media'
  ];

  var revealEls = [];
  revealSel.forEach(function (sel) {
    each(document.querySelectorAll(sel), function (el, i) {
      if (el.classList.contains('psm-left') || el.classList.contains('psm-right')) { return; }
      el.classList.add(i % 2 === 0 ? 'psm-left' : 'psm-right');
      revealEls.push(el);
    });
  });

  if (!revealEls.length) { return; }

  var revealIO = new IntersectionObserver(function (entries) {
    entries.forEach(function (en) {
      if (!en.isIntersecting) { return; }
      var el = en.target;
      var siblings = Array.prototype.filter.call(
        el.parentElement ? el.parentElement.children : [],
        function (c) {
          return c.classList.contains('psm-left') || c.classList.contains('psm-right');
        }
      );
      var idx = siblings.indexOf(el);
      window.setTimeout(function () { el.classList.add('psm-in'); }, Math.max(0, idx) * 90);
      revealIO.unobserve(el);
    });
  }, { threshold: 0.18, rootMargin: '0px 0px -6% 0px' });

  revealEls.forEach(function (el) { revealIO.observe(el); });

  /* safety net: a reveal class that never fires would leave content at
     opacity 0. After 4 s everything is shown no matter what. */
  window.setTimeout(function () {
    revealEls.forEach(function (el) { el.classList.add('psm-in'); });
  }, 4000);
})();
